<template>
    <div class="content">
        <div class="container-fluid">
            <!-- Titre & breadcrumb -->
            <div class="page-heading row justify-content-between align-items-center py-3">
                <div class="col-lg-4">
                    <h4 class="page-title">{{ $t('installationList.title') }}</h4>
                </div>
            </div>

            <!-- Table [start] -->
            <div class="card">
                <div class="card-header">
                    <div class="row align-items-center">
                        <div class="col-sm-6 col-md-4">
                            <h4 class="header-title">{{ $t('installationList.listTitle') }}</h4>
                        </div>
                        <div class="col-md-8 mt-2 mt-md-0 text-end">
                            <div class="d-flex justify-content-end gap-2">
                                <input id="searchTrigger" type="text"
                                    class="form-control form-control-sm search-trigger" :placeholder="$t('common.search')"
                                    maxlength="64" />
                                <button type="button" class="btn btn-primary" @click="openCreateInstallation">
                                    <i class="mdi mdi-plus"></i><span class="d-none d-xxl-inline-block">{{ $t('common.add') }}</span>
                                </button>
                                <button type="button" class="btn btn-primary" :title="$t('common.refresh')" :disabled="refreshing" @click="refreshInstallations">
                                    <i class="mdi mdi-refresh" :class="{ 'mdi-spin': refreshing }"></i><span class="d-none d-xxl-inline-block">{{ $t('common.refresh') }}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="card-body pt-0">
                    <table id="installations-table" :key="tableKey" class="table table-centered table-striped">
                        <thead>
                            <tr>
                                <th scope="col">{{ $t('installationList.columns.installation') }}</th>
                                <th scope="col">{{ $t('installationList.columns.owner') }}</th>
                                <th scope="col">{{ $t('installationList.columns.location') }}</th>
                                <th scope="col">{{ $t('installationList.columns.creationDate') }}</th>
                                <th scope="col">{{ $t('installationList.columns.components') }}</th>
                                <th scope="col">&nbsp;</th>
                            </tr>
                        </thead>
                        <tbody>
                            <InstallationRow v-for="facility in facilities" :key="facility.id" :facility="facility"
                                :openDeleteModal="openDeleteModal" />
                        </tbody>
                    </table>
                </div>
                <!-- Table [end] -->
            </div>
        </div>
    </div>
    <ConfirmationModal ref="confirmDeleteModal" :title="$t('common.confirmDelete')" :question="$t('installationList.confirmDeleteQuestion')" type="danger" :button1="$t('common.cancel')" :button2="$t('common.delete')" :action="deleteInstallation" />
</template>

<script>
import { useUserStore } from '@/stores/userStore';
import { showToast } from '@/services/toast';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import InstallationRow from '@/components/installationRow.vue';
import DataTable from 'datatables.net';
import ConfirmationModal from '@/modals/confirmationModal.vue';

export default {
    components: {
        InstallationRow,
        ConfirmationModal,
    },
    setup() {
        const userStore = useUserStore();
        const { t, locale } = useI18n();
        const router = useRouter();
        const confirmDeleteModal = ref(null);
        let datatable = null;

        const facilities = computed(() => userStore.facilities);

        function initDataTable() {
            const table = document.getElementById('installations-table');
            if (!table) return;

            const config = {
                dom: 'lrtip',
                order: [[0, 'asc']],
                pageLength: 50,
                lengthChange: false,
                columnDefs: [
                    { orderable: false, targets: [5] },
                    { type: 'date-dd-mm-yyyy', targets: [4] }
                ],
                paging: false,
                info: false,
                language: {
                    searchPlaceholder: t('common.search'),
                    emptyTable: `<div style='text-align:center;'>${t('installationList.emptyTable')}</div>`,
                    zeroRecords: `<div style='text-align:center;'>${t('datatables.zeroRecords')}</div>`,
                    processing: t('datatables.processing'),
                    search: t('datatables.search'),
                    lengthMenu: t('datatables.lengthMenu'),
                    info: t('datatables.info'),
                    infoEmpty: t('datatables.infoEmpty'),
                    infoFiltered: t('datatables.infoFiltered'),
                    loadingRecords: t('datatables.loadingRecords'),
                },
                headerCallback: function (thead) {
                    const ths = thead.getElementsByTagName('th');
                    for (let i = 0; i < ths.length; i++) {
                        ths[i].innerHTML = ths[i].textContent;
                    }
                },
                drawCallback: function () {
                    const pagination = document.querySelector('.dataTables_paginate > .pagination');
                    if (pagination) {
                        pagination.classList.add('pagination-rounded');
                    }
                }
            };

            if (locale.value === 'fr') {
                config.language.url = 'assets/data/dataTables.french.lang';
            }

            datatable = new DataTable(table, config);

            const searchInput = document.getElementById('searchTrigger');
            searchInput.addEventListener('keyup', function() {
                datatable.search(this.value).draw();
            });
            if (searchInput.value) {
                datatable.search(searchInput.value).draw();
            }
        }

        const tableKey = ref(0);

        function destroyDataTable() {
            if (datatable) {
                datatable.destroy();
                datatable = null;
            }
        }

        async function reinitDataTable() {
            destroyDataTable();
            // Force le re-rendu complet du tableau : destroy() restaure le DOM
            // mémorisé par DataTables, qui peut contenir des lignes obsolètes
            tableKey.value++;
            await nextTick();
            initDataTable();
        }

        onMounted(() => {
            initDataTable();
        });

        watch(locale, () => {
            reinitDataTable();
        });

        function openDeleteModal(id) {
            confirmDeleteModal.value.show(id);
        }

        async function deleteInstallation(id) {
            try {
                await userStore.deleteFacility(id);
                await reinitDataTable();
                showToast('success', t('installationList.deleteSuccess'));
            } catch(error) {
                showToast('error', error);
            }
        }

        function openCreateInstallation() {
            router.push({ path: '/installation-create'});
        }

        const refreshing = ref(false);

        async function refreshInstallations() {
            if (refreshing.value) return;
            refreshing.value = true;
            try {
                // Le tableau doit être rendu à Vue avant la mise à jour des données :
                // Vue ne sait pas patcher des lignes que DataTables a déplacées
                destroyDataTable();
                tableKey.value++;
                await nextTick();
                await userStore.loadInstallerFacilities();
            } catch (error) {
                showToast('error', error);
            } finally {
                await nextTick();
                initDataTable();
                refreshing.value = false;
            }
        }

        return {
            facilities,
            confirmDeleteModal,
            openDeleteModal,
            deleteInstallation,
            openCreateInstallation,
            refreshing,
            refreshInstallations,
            tableKey,
        };
    }
};
</script>

<style scoped>
table.dataTable tbody tr td.dataTables_empty {
    text-align: center;
    font-style: italic;
    color: #999;
}
</style>
